import { buildSupabaseFunctionUrl, resolveOpenRouterChatUrl } from '@/lib/url';
const API_URL = resolveOpenRouterChatUrl(import.meta.env.VITE_OPENROUTER_ENDPOINT);
const API_KEY = import.meta.env.VITE_OPENROUTER_API_KEY;
const SUPABASE_URL = buildSupabaseFunctionUrl(import.meta.env.VITE_SUPABASE_URL, 'prompt-ai');
const SUPABASE_KEY = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;


const MODELS = [
  'meta-llama/llama-3-8b-instruct:free',
  'mistralai/mistral-7b-instruct:free',
  'google/gemma-7b-it:free',
] as const;

interface PromptSettingsContext {
  cameraAngle?: string;
  lightingStyle?: string;
  shotSize?: string;
  lens?: string;
  composition?: string;
  depthOfField?: string;
  smartDefaults?: string;
}

const CONFLICT_SYSTEM_PROMPT = `You are an AI prompt analyzer.

Analyze the given AI image prompt and detect all logical conflicts.

Check for:

camera conflicts
shot size contradictions
lighting direction conflicts
depth of field contradictions
lens vs shot mismatches
composition contradictions
duplicate prompt segments
broken sentence fragments
incomplete phrases
impossible cinematic instructions

Return a structured analysis of the detected conflicts.`;

const FIX_SYSTEM_PROMPT = `You are a cinematic prompt repair engine.

Your task is to repair the prompt while preserving its core subject.

Rules:

1. Do NOT change the scene topic.
2. Remove contradictions and logical conflicts.
3. Repair broken sentences.
4. Remove duplicated fragments.
5. Fix camera, lighting, and shot inconsistencies.
6. Preserve the visual idea.

Output rules:

Return ONLY the repaired prompt.

Do not add explanations.
Do not add comments.
Do not add headings.`;

const ENHANCE_SYSTEM_PROMPT = `You are an expert cinematic prompt engineer.

Your task is to transform the prompt into a highly professional cinematic prompt.

Rules:

1. Preserve the original subject and scene.
2. Improve visual clarity.
3. Improve cinematic realism.
4. Upgrade camera details naturally.
5. Improve lighting logic.
6. Improve scene storytelling.

Important:

Do NOT use a fixed template.
Adapt the prompt structure to the scene.

Output rules:

Return ONLY the enhanced prompt.
No explanations.`;

const GENERATE_SYSTEM_PROMPT = `You are an advanced cinematic prompt generator.

Your task is to understand the user's description and transform it into a cinematic AI image prompt.

Rules:

1. Do NOT use a fixed template.
2. Adapt the prompt structure to the scene.
3. Maintain cinematic realism.
4. Choose camera settings that match the scene naturally.
5. Avoid repeating the same wording across prompts.
6. Do not output placeholders.
7. Generate a natural flowing prompt.

Return only the final prompt.`;


const EDIT_SYSTEM_PROMPT = `You are a prompt editor.

You understand Arabic, English, and Arabic dialects.
Interpret user instructions even if they are informal.

Modify the existing prompt according to the user's instruction.

Rules:

* Keep the original scene.
* Apply the user's change only.
* Preserve all other details.

Return ONLY the updated prompt.`;

const normalizeText = (value: string): string => value
  .replace(/\s+/g, ' ')
  .replace(/,{2,}/g, ',')
  .replace(/\s*,\s*/g, ', ')
  .replace(/\s+([.;:!?])/g, '$1')
  .trim();

const withTimeout = async <T>(promise: Promise<T>, timeoutMs = 25000): Promise<T> => {
  let timeoutId: ReturnType<typeof setTimeout> | undefined;
  const timeoutPromise = new Promise<never>((_, reject) => {
    timeoutId = setTimeout(() => reject(new Error('OpenRouter timeout')), timeoutMs);
  });

  try {
    return await Promise.race([promise, timeoutPromise]);
  } finally {
    if (timeoutId) clearTimeout(timeoutId);
  }
};

const extractContent = (payload: any): string => {
  const content = payload?.choices?.[0]?.message?.content;
  return typeof content === 'string' ? normalizeText(content) : '';
};


const callPromptAI = async (mode: 'analyze' | 'fix' | 'enhance', prompt: string): Promise<any | null> => {
  if (!SUPABASE_URL || !SUPABASE_KEY) return null;
  try {
    const res = await withTimeout(fetch(SUPABASE_URL, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${SUPABASE_KEY}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ mode, prompt }),
    }));
    if (!res.ok) return null;
    return await res.json();
  } catch {
    return null;
  }
};

export async function callOpenRouter(userPrompt: string, systemPrompt: string, temperature = 0.4): Promise<string> {
  if (!API_KEY) throw new Error('Missing VITE_OPENROUTER_API_KEY');

  for (const model of MODELS) {
    try {
      const res = await withTimeout(fetch(API_URL, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${API_KEY}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          model,
          messages: [
            { role: 'system', content: systemPrompt },
            { role: 'user', content: userPrompt },
          ],
          temperature,
        }),
      }));

      if (!res.ok) continue;
      const data = await res.json();
      const text = extractContent(data);
      if (text) return text;
    } catch {
      console.warn('model failed', model);
    }
  }

  throw new Error('All models failed');
}

export async function detectConflicts(prompt: string): Promise<{ hasConflicts: boolean; summary: string }> {
  if (!prompt.trim()) return { hasConflicts: false, summary: '' };

  const edge = await callPromptAI('analyze', prompt);
  const issues = Array.isArray(edge?.issues) ? edge.issues : [];
  if (edge) {
    const summary = issues.length
      ? issues.map((i: any) => `${i.type || 'issue'}: ${i.reason || i.text_span || ''}`).join(' | ')
      : 'No conflicts detected';
    return { hasConflicts: issues.length > 0, summary };
  }

  const summary = await callOpenRouter(prompt, CONFLICT_SYSTEM_PROMPT);
  const lowered = summary.toLowerCase();
  const hasConflicts = !(/no conflict|no conflicts|clean prompt|stable prompt/.test(lowered));
  return { hasConflicts, summary };
}

export async function fixPrompt(prompt: string, settings?: PromptSettingsContext): Promise<string> {
  if (!prompt.trim()) return '';

  const settingsBlock = settings
    ? [
      settings.cameraAngle ? `Camera angle: ${settings.cameraAngle}` : '',
      settings.lightingStyle ? `Lighting: ${settings.lightingStyle}` : '',
      settings.shotSize ? `Shot size: ${settings.shotSize}` : '',
      settings.lens ? `Lens: ${settings.lens}` : '',
      settings.depthOfField ? `Depth of field: ${settings.depthOfField}` : '',
      settings.composition ? `Composition: ${settings.composition}` : '',
      settings.smartDefaults ? `Smart defaults: ${settings.smartDefaults}` : '',
    ].filter(Boolean).join('\n')
    : '';

  const userPrompt = settingsBlock
    ? `Current prompt:
${prompt}

Settings:
${settingsBlock}

Instruction:
Repair this prompt while preserving scene and respecting these settings.`
    : prompt;

  const edge = await callPromptAI('fix', userPrompt);
  const fixed = typeof edge?.fixed_prompt === 'string' ? normalizeText(edge.fixed_prompt) : '';
  if (fixed) return fixed;

  return callOpenRouter(userPrompt, FIX_SYSTEM_PROMPT);
}

export async function enhancePrompt(prompt: string, settings?: PromptSettingsContext): Promise<string> {
  if (!prompt.trim()) return '';

  const settingsBlock = settings
    ? [
      settings.cameraAngle ? `Camera angle: ${settings.cameraAngle}` : '',
      settings.lightingStyle ? `Lighting: ${settings.lightingStyle}` : '',
      settings.shotSize ? `Shot size: ${settings.shotSize}` : '',
      settings.lens ? `Lens: ${settings.lens}` : '',
      settings.depthOfField ? `Depth of field: ${settings.depthOfField}` : '',
      settings.composition ? `Composition: ${settings.composition}` : '',
      settings.smartDefaults ? `Smart defaults: ${settings.smartDefaults}` : '',
    ].filter(Boolean).join('\n')
    : '';

  const userPrompt = settingsBlock
    ? `Current prompt:
${prompt}

Settings:
${settingsBlock}

Instruction:
Enhance this prompt professionally while respecting these settings.`
    : prompt;

  const edge = await callPromptAI('enhance', userPrompt);
  const enhanced = typeof edge?.enhanced_prompt === 'string' ? normalizeText(edge.enhanced_prompt) : '';
  if (enhanced) return enhanced;

  return callOpenRouter(userPrompt, ENHANCE_SYSTEM_PROMPT);
}


export async function generatePromptFromDescription(userDescription: string): Promise<string> {
  if (!userDescription.trim()) return '';
  return callOpenRouter(userDescription, GENERATE_SYSTEM_PROMPT, 0.7);
}


export async function editPromptSequential(currentPrompt: string, userInstruction: string): Promise<string> {
  const cleanCurrent = currentPrompt.trim();
  const cleanInstruction = userInstruction.trim();
  if (!cleanInstruction) return cleanCurrent;
  if (!cleanCurrent) return generatePromptFromDescription(cleanInstruction);

  const userMessage = `Current prompt:
${cleanCurrent}

User instruction:
${cleanInstruction}`;
  return callOpenRouter(userMessage, EDIT_SYSTEM_PROMPT, 0.5);
}
